import dynamic from "next/dynamic";
import React, { useEffect, useState } from "react";
import useDebounce from "@deosite/lib/useDebounce";
import PropTypes from "prop-types";
import Box from "@commerce-ui/core2/Box";
import { useRecentlyViewedProducts } from "@deosite/api/hooks/common/useRecentlyViewedProducts";
import { useRouter } from "next/router";
import Footer from "../../../sections/Footer";
import RecentlyViewed from "../../../components/RecentlyViewed";
import ReturnText from "../../../components/ReturnText";
import Container from "@commerce-ui/core2/Container";
import { useMinicart } from "@deosite/api/hooks/common/useMinicart";
import Loader from "@deosite/components-new/Loader";
import { useLoader } from "@deosite/api/hooks/common/useLoader";
import SelectInDialog from "@deosite/components-new/SelectInDialog";
import { useLanguage } from "@deosite/api/hooks/common/LanguageProvider";

import translate from "@deosite/helpers/langLibrary";
import useAnalyticsAndCommerceTags from "@deosite/api/hooks/common/useAnalytics4AndCommerceTags";

import NewsletterPopup from "@deosite/components-new/NewsletterPopup";
const Dialog = dynamic(() => import("@commerce-ui/core2/Dialog"));
const MinicartContent = dynamic(() =>
  import("../../../components/MinicartContent")
);
const Minicart = dynamic(() => import("../../../sections/Minicart"));
const PriceProgress = dynamic(() =>
  import("../../../components/PriceProgress")
);
const OrderSummary = dynamic(() => import("../../../components/OrderSummary"));
const ProductRow = dynamic(() => import("../../../components/ProductRow"));

const addScrollToPage = () => {
  document.getElementsByTagName("body")[0].style =
    "margin:0;overflow-x:hidden;-webkit-overflow-scrolling:touch";
};
const PageContainer = props => {
  const {
    children,
    returnLinkLabel,
    onReturnLabelClick,
    recentlyViewedActive,
    footerActive,
    footerContent,
    dataRequiredToHideLoader,
    externalIntegrationsHtml,
    skipFirstSectionMarginOnMobile = false
  } = props;

  const router = useRouter();
  const { changeLanguage, language } = useLanguage();

  const translations = {
    languages: translate("languages"),
    select: translate("select"),
    cookieNotice: translate("Cookie notice"),
    accept: translate("Accept"),
    advertSlogan: translate("Subscribe to our newsletter"),
    advertText: translate("And get even 10% off"),
    subscribe: translate("Subscribe")
  };

  const [minicartLoaded, setMinicartLoaded] = useState(null);
  const [showImportComponent, setShowImportComponent] = useState(null);

  const lang = (
    <Box>
      <SelectInDialog
        options={Object.values(global.__appConfig__.domainLanguages)}
        value={language.language}
        label={translations.languages.toUpperCase()}
        placeholder={translations.select}
        onChange={lang => changeLanguage(lang)}
        textTransform={"uppercase"}
        colorLabel={"beigeDarken"}
        borderColor={"beigeDarken"}
        valueMarginTop={"s6"}
        height={"45px"}
        borderRadius={"0px"}
        colorArrow={"beigeDarken"}
        colorOptionSelect={"neutral"}
        my={"0px"}
        pl={"10px"}
        font={"s_sans"}
      />
    </Box>
  );
  const sectionBottomSpace = { mb: ["s11", null, null, "s12", null, null] };
  const skipMobileSectionBottomSpace = {
    mb: [0, null, null, "s12", null, null]
  };
  const recentlyViewedProducts = useRecentlyViewedProducts();
  const {
    isMinicartVisible,
    toggleMinicartVisibility,
    minicartContent,
    itemsAmount: itemsAmountInMinicart,
    minicartTotalPrice,
    showDiscountInProductRow
  } = useMinicart();
  const { isLoading } = useLoader();

  const { viewCart } = useAnalyticsAndCommerceTags();

  useEffect(() => {
    if (isMinicartVisible && !minicartLoaded) {
      setMinicartLoaded(true);
    }
    if (!isMinicartVisible) {
      addScrollToPage();
    }
    isMinicartVisible && viewCart(minicartContent);
  }, [isMinicartVisible]);

  useEffect(() => {
    if (isMinicartVisible) {
      toggleMinicartVisibility();
      addScrollToPage();
    }
  }, [router.asPath]);

  const showMinicart = () => (
    <Dialog
      isOpen={isMinicartVisible}
      onRequestClose={toggleMinicartVisibility}
      anchoredTo={"window"}
      sx={{
        width: ["calc(100%)", null, null, "calc(50%)", "calc(35%)", null],
        placement: "right"
      }}
      onClose={() => {
        if (!isMinicartVisible) return;
        toggleMinicartVisibility({ close: true });
      }}
    >
      <Minicart itemsAmountInCart={itemsAmountInMinicart}>
        <MinicartContent
          itemsAmountInCart={itemsAmountInMinicart}
          progressBar={
            <PriceProgress
              currentPrice={minicartTotalPrice()}
              limitPrice={
                global.__appConfig__.shippingLimitPrices[language.language]
              }
            />
          }
          productRows={minicartContent
            ?.filter(item => item?.product)
            ?.map(item => (
              <ProductRow
                product={item.product}
                quantity={item.quantity}
                onLinkClick={toggleMinicartVisibility}
                showDiscountFromTotals={showDiscountInProductRow}
              />
            ))}
          orderSummary={
            <OrderSummary
              total={minicartTotalPrice()}
              vatLabel={true}
              isMinicart={true}
            />
          }
        />
      </Minicart>
    </Dialog>
  );

  const importComponentsDebounce = useDebounce(
    parseInt(global.__appConfig__.submenuLoadTimeout),
    () => {
      setShowImportComponent(true);
    }
  );

  const importComponents = () => {
    return (
      <>
        <Dialog />
        <Minicart />
        <MinicartContent />
        <OrderSummary />
        <PriceProgress />
      </>
    );
  };

  return (
    <Box>
      {minicartLoaded ? (
        showMinicart()
      ) : (
        <Box sx={{ display: "none" }}>
          {showImportComponent
            ? importComponents()
            : importComponentsDebounce()}
        </Box>
      )}

      {returnLinkLabel && (
        <Box sx={{ mt: "s7", mb: "s8" }}>
          <Container>
            <ReturnText label={returnLinkLabel} onClick={onReturnLabelClick} />
          </Container>
        </Box>
      )}

      {!dataRequiredToHideLoader.every(param => param !== undefined) ||
      isLoading ? (
        <Loader />
      ) : (
        React.Children.toArray(children).map((child, index) => {
          return (
            <Box
              sx={
                skipFirstSectionMarginOnMobile && index === 0
                  ? skipMobileSectionBottomSpace
                  : sectionBottomSpace
              }
              key={index}
            >
              {child}
            </Box>
          );
        })
      )}

      {recentlyViewedActive &&
        recentlyViewedProducts &&
        recentlyViewedProducts.length > 0 && (
          <Box sx={sectionBottomSpace}>
            <RecentlyViewed products={recentlyViewedProducts} />
          </Box>
        )}
      {footerActive && (
        <Footer
          additionalSeparator={false}
          additionalFont={"xs"}
          footerContent={footerContent}
          languages={lang}
        />
      )}
      <NewsletterPopup
        placement={"center"}
        width={900}
        advertSlogan={translations.advertSlogan}
        advertText={translations.advertText}
        buttonLabel={translations.subscribe}
        advertLink={"/newsletter/signup"}
      />
      <Box
        dangerouslySetInnerHTML={{
          __html: language?.language === "pl" ? externalIntegrationsHtml : ""
        }}
      />
    </Box>
  );
};

PageContainer.defaultProps = {
  recentlyViewedActive: true,
  footerActive: true,
  dataRequiredToHideLoader: [],
  externalIntegrationsHtml: ""
};

PageContainer.propTypes = {
  cart: PropTypes.array,
  router: PropTypes.object,
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  contentBlocks: PropTypes.array,
  socialMediaElements: PropTypes.array,
  logo: PropTypes.object,
  returnLinkLabel: PropTypes.string,
  onReturnLabelClick: PropTypes.func,
  metaTagsData: PropTypes.object,
  recentlyViewedActive: PropTypes.bool,
  footerActive: PropTypes.bool,
  dataRequiredToHideLoader: PropTypes.array,
  externalIntegrationsHtml: PropTypes.string
};

export default PageContainer;
